import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
  untracked,
} from "@angular/core";
import { VideoSearchStore } from "../../store/video-search-store";
import { Router } from "@angular/router";
import { FilterResultSection } from "src/app/shared/components/filter-result-section/filter-result-section";
import { CardResult } from "src/app/shared/components/card-result/card-result";
import { Button } from "src/app/shared/components/utils/button/button";
import { Loader } from "src/app/shared/components/loader/loader";
import { TranslocoModule } from "@jsverse/transloco";
import { VideoTileDto } from "src/app/core/interfaces/tile-interface";
import { LanguageFilter } from "src/app/shared/components/language-filter/language-filter";
import { FavoriteTileErrorModal } from "src/app/shared/components/favorite-tile-error-modal/favorite-tile-error-modal";
import { LanguageService } from "src/app/core/services/language-service";

@Component({
  selector: "app-video-result-page",
  imports: [
    FilterResultSection,
    CardResult,
    Button,
    Loader,
    TranslocoModule,
    LanguageFilter,
    FavoriteTileErrorModal,
  ],
  templateUrl: "./video-result-page.html",
})
export class VideoResultPage implements OnDestroy {
  private readonly languageService = inject(LanguageService);

  router = inject(Router);

  videoStore = inject(VideoSearchStore);

  loading = signal<boolean>(false);
  extraResultLoading = signal<boolean>(false);

  languagesSelected = signal<string[]>([]);

  shownVideos = signal<VideoTileDto[]>([]);

  availableLanguages = computed(() => {
    const results = this.videoStore.results();
    const languages = new Set<string>();

    results.forEach((video) => {
      video.languages.forEach((lang) => {
        const matchedLanguages = this.videoStore
          .languageFilters()
          .filters.filter(
            (languageFilter) => languageFilter.translation === lang
          );
        if (matchedLanguages.length > 0) {
          matchedLanguages.forEach((matchedLanguage) => {
            languages.add(matchedLanguage.translation);
          });
        }
      });
    });

    return Array.from(languages).sort((a, b) => a.localeCompare(b));
  });

  totalVideosFound = computed(() => this.videoStore.totalVideosFound());

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();

      untracked(async () => {
        this.loading.set(true);
        await this.videoStore.loadVideoFilters();
        this.videoStore.updateSelectedFiltersTranslations();
        try {
          await this.videoStore.loadVideos();
          this.shownVideos.set(this.videoStore.results());
        } finally {
          this.loading.set(false);
        }
      });
    });
  }

  onLanguagesChanged(selectedLanguages: string[]): void {
    this.languagesSelected.set(selectedLanguages);

    if (selectedLanguages.length === 0) {
      this.shownVideos.set(this.videoStore.results());
    } else {
      this.shownVideos.set(
        this.videoStore
          .results()
          .filter((video) =>
            selectedLanguages.some((lang) => video.languages.includes(lang))
          )
      );
    }
  }

  async loadMoreVideoTiles(): Promise<void> {
    this.videoStore.pagination().start = this.videoStore.pagination().start + 1;
    try {
      this.extraResultLoading.set(true);
      await this.videoStore.loadMoreVideos();
      this.shownVideos.set(this.videoStore.results());
    } finally {
      this.extraResultLoading.set(false);
    }
  }

  redirectToVideoResearch(): void {
    this.router.navigate(["/mobility/videos-search"]);
  }

  ngOnDestroy() {
    this.videoStore.setResults([]);
    this.videoStore.setPagination(null);
    document.body.style.overflow = "";
  }
}
